import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import FaqSection from '../components/common/FaqSection';
import AdSlot from '../components/advertisement/AdSlot';
import SubscriptionHeaderCard from '../components/subscription-status/SubscriptionHeaderCard';
import SubscriptionControls from '../components/subscription-status/SubscriptionControls';
import LiveBiddingTable from '../components/subscription-status/LiveBiddingTable';
import SubscriptionMetricsGuide from '../components/subscription-status/SubscriptionMetricsGuide';
import { SUBSCRIPTION_FAQS } from '../data/ipoSubscriptionData';
import {
  SUBSCRIPTION_TABS,
  buildSubscriptionCsv,
  computeSubscriptionStats,
  filterSubscriptions,
  latestSync,
  subscriptionTabByKey,
  toSubscriptionStatusRow,
} from '../utils/ipoSubscription';
import { fetchSubscriptionSummary, selectSubscriptionRows } from '../store/slices/subscriptionSlice';

const PAGE_SIZE = 16;

const HEADINGS = {
  mainboard: 'Mainboard IPOs: Live Subscription Status (BSE & NSE)',
  sme: 'SME IPOs: Live Subscription Status (BSE SME & NSE Emerge)',
  closed: 'Closed IPOs: Final Subscription Status (BSE & NSE)',
};

export const IpoSubscriptionPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = subscriptionTabByKey(searchParams.get('tab')).key;
  const [demand, setDemand] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  // GET /api/market/subscription -- shared with the homepage's subscription
  // section, so navigating between the two reuses the cached response
  const subscriptionRows = useSelector(selectSubscriptionRows);
  const { refreshedAt, status, error } = useSelector((state) => state.subscription);
  const SUBSCRIPTION_ISSUES = useMemo(
    () => subscriptionRows.map((row) => toSubscriptionStatusRow(row, refreshedAt)),
    [subscriptionRows, refreshedAt]
  );
  const isLoading = !SUBSCRIPTION_ISSUES.length && (status === 'idle' || status === 'loading');
  const isFailed = !SUBSCRIPTION_ISSUES.length && status === 'failed';

  useEffect(() => {
    dispatch(fetchSubscriptionSummary({ maxAgeMs: 60000 }));
  }, [dispatch]);

  const LAST_SYNC = useMemo(() => latestSync(SUBSCRIPTION_ISSUES), [SUBSCRIPTION_ISSUES]);
  const LISTING_YEAR = LAST_SYNC ? LAST_SYNC.slice(0, 4) : String(new Date().getFullYear());
  const TAB_COUNTS = useMemo(
    () => Object.fromEntries(SUBSCRIPTION_TABS.map((t) => [t.key, SUBSCRIPTION_ISSUES.filter(t.test).length])),
    [SUBSCRIPTION_ISSUES]
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADINGS[tab]} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [tab]);

  useEffect(() => {
    setPage(1);
  }, [tab, demand, query]);

  const tabIssues = useMemo(() => SUBSCRIPTION_ISSUES.filter(subscriptionTabByKey(tab).test), [SUBSCRIPTION_ISSUES, tab]);
  const stats = useMemo(() => computeSubscriptionStats(tabIssues), [tabIssues]);
  const rows = useMemo(
    () => filterSubscriptions(SUBSCRIPTION_ISSUES, { tab, demand, query }),
    [SUBSCRIPTION_ISSUES, tab, demand, query]
  );

  const changeTab = (key) => setSearchParams(key === 'mainboard' ? {} : { tab: key }, { replace: true });

  const handleExport = () => {
    // BOM so spreadsheet apps read the file as UTF-8
    const blob = new Blob(['﻿', buildSubscriptionCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ipo-subscription-${tab}-${LISTING_YEAR}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const resetFilters = () => {
    setDemand('all');
    setQuery('');
  };

  let emptyMessage;
  if (isLoading) {
    emptyMessage = 'Loading live subscription data from NSE…';
  } else if (isFailed) {
    emptyMessage = (
      <>
        Subscription data is temporarily unavailable{error ? ` (${error})` : ''}.{' '}
        <button type="button" onClick={() => dispatch(fetchSubscriptionSummary())} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!tabIssues.length) {
    emptyMessage =
      tab === 'closed'
        ? 'No closed issues in the current NSE feed.'
        : `No ${tab === 'sme' ? 'SME' : 'mainboard'} IPOs are open for bidding right now. Check the IPO calendar for upcoming dates.`;
  } else {
    emptyMessage = (
      <>
        No IPOs match these filters.{' '}
        <button type="button" onClick={resetFilters} className="font-bold text-[#008080] hover:underline">
          Show all
        </button>
      </>
    );
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): page slot first, then the home leaderboard */}
        <OfferStrip slotId={['ipo-subscription-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
            <Link to="/" className="hover:text-[#008080] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
              IPO Reports
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-900 font-semibold">Live Subscription Status ({LISTING_YEAR})</span>
          </nav>

          <SubscriptionHeaderCard
            heading={HEADINGS[tab]}
            stats={stats}
            lastSync={LAST_SYNC}
            year={LISTING_YEAR}
            onExport={handleExport}
            canExport={rows.length > 0}
          />

          <SubscriptionControls
            tab={tab}
            onTabChange={changeTab}
            tabCounts={TAB_COUNTS}
            demand={demand}
            onDemandChange={setDemand}
            query={query}
            onQueryChange={setQuery}
          />

          <LiveBiddingTable
            issues={rows}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            emptyMessage={emptyMessage}
          />

          <SubscriptionMetricsGuide />

          <FaqSection
            id="subscription-faq"
            title="Frequently Asked Questions (FAQs)"
            subtitle="Clear answers on live IPO bidding numbers, formulas and exchange reconciliation."
            faqs={SUBSCRIPTION_FAQS}
          />

          {/* Partner ad (GET /api/ads): page slot first, then the home in-feed */}
          <AdSlot slotId={['ipo-subscription-bottom', 'infeed']} variant="feature" />
        </div>
      </main>
    </>
  );
};

export default IpoSubscriptionPage;
